import React, { useState, useEffect, useReducer, useContext, useCallback } from "react";
import { toast } from "react-toastify";
import n8n from "../../assets/n8n.png";
import dialogflow from "../../assets/dialogflow.png";
import webhooks from "../../assets/webhook.png";
import typebot from "../../assets/typebot.jpg";
import flowbuilder from "../../assets/flowbuilders.png";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  CardActions,
  Chip,
  Grid,
} from "@material-ui/core";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import SearchIcon from "@material-ui/icons/Search";
import AddIcon from "@material-ui/icons/Add";
import SettingsIcon from "@material-ui/icons/Settings";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import IntegrationModal from "../../components/QueueIntegrationModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import GoogleCalendarIntegrationModal from "../../components/GoogleCalendarIntegrationModal";
import FlowBuilderModal from "../../components/FlowBuilderModal";
import FlowBuilderIntegrationModal from "../../components/FlowBuilderIntegrationModal";
import WebhookModal from "../../components/WebhookModal";
import AsaasModal from "../../components/AsaasModal";
import MercadoPagoModal from "../../components/MercadoPagoModal";
import SgpModal from "../../components/SgpModal";
import SysClinicaModal from "../../components/SysClinicaModal";
import SysAcademiaModal from "../../components/SysAcademiaModal";
import HostiSitesModal from "../../components/HostiSitesModal";
import InfinityPayModal from "../../components/InfinityPayModal";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import usePlans from "../../hooks/usePlans";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import ForbiddenPage from "../../components/ForbiddenPage";
import braipLogo from "../../assets/braip.svg";
import paytLogo from "../../assets/payt.png";
import ConectorAssistentes from "../../components/ConectorAssistentes";
import logoCodex from "../../assets/logo-codex.png";
import logoClaude from "../../assets/logo-claude.png";
import FileCopyIcon from "@material-ui/icons/FileCopy";

const reducer = (state, action) => {
  if (action.type === "LOAD_INTEGRATIONS") {
    const queueIntegration = action.payload;
    const newIntegrations = [];

    queueIntegration.forEach((integration) => {
      const integrationIndex = state.findIndex((u) => u.id === integration.id);
      if (integrationIndex !== -1) {
        state[integrationIndex] = integration;
      } else {
        newIntegrations.push(integration);
      }
    });

    return [...state, ...newIntegrations];
  }

  if (action.type === "UPDATE_INTEGRATIONS") {
    const queueIntegration = action.payload;
    const integrationIndex = state.findIndex((u) => u.id === queueIntegration.id);

    if (integrationIndex !== -1) {
      state[integrationIndex] = queueIntegration;
      return [...state];
    } else {
      return [queueIntegration, ...state];
    }
  }

  if (action.type === "DELETE_INTEGRATION") {
    const integrationId = action.payload;

    const integrationIndex = state.findIndex((u) => u.id === integrationId);
    if (integrationIndex !== -1) {
      state.splice(integrationIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    backgroundColor: "transparent",
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px 24px",
    borderBottom: "1px solid #e0e0e0",
    flexWrap: "wrap",
    gap: 12,
    [theme.breakpoints.down("sm")]: { padding: "12px 16px", flexDirection: "column", alignItems: "stretch" },
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: 16,
  },
  headerTitle: {
    fontSize: "1.5rem",
    fontWeight: 600,
    color: "#1a1a1a",
    [theme.breakpoints.down("sm")]: { fontSize: "1.2rem" },
  },
  headerSubtitle: {
    fontSize: "0.875rem",
    color: "#666",
  },
  headerRight: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: { flexDirection: "column", alignItems: "stretch" },
  },
  searchField: {
    backgroundColor: "#fff",
    borderRadius: 8,
    minWidth: 220,
    [theme.breakpoints.down("sm")]: { minWidth: "unset", width: "100%" },
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
      "& fieldset": { borderColor: "#e0e0e0" },
      "&:hover fieldset": { borderColor: "#1976d2" },
    },
  },
  addButton: {
    borderRadius: 8,
    padding: "6px 20px",
    textTransform: "none",
    fontWeight: 600,
    [theme.breakpoints.down("sm")]: { width: "100%" },
  },
  googleButton: {
    borderRadius: 8,
    padding: "6px 16px",
    textTransform: "none",
    fontWeight: 600,
    [theme.breakpoints.down("sm")]: { width: "100%" },
  },
  content: {
    padding: "0 24px 16px",
    [theme.breakpoints.down("sm")]: { padding: "0 12px 16px" },
  },
  statusCard: {
    borderRadius: 12,
    padding: 16,
    backgroundColor: "#f1f8e9",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 16,
  },
  statusInfo: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  tableWrapper: {
    backgroundColor: "#fff",
    borderRadius: 12,
    overflow: "hidden",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
  },
  tableHead: {
    backgroundColor: "var(--sidebar-color, #1e293b)",
    "& th": {
      color: "#cbd5e1",
      fontWeight: 600,
      fontSize: "0.8rem",
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      borderBottom: "none",
      padding: "14px 16px",
    },
  },
  tableBody: {
    "& td": {
      padding: "12px 16px",
      fontSize: "0.875rem",
      color: "#334155",
      borderBottom: "1px solid #f1f5f9",
    },
    "& tr:hover": {
      backgroundColor: "#f8fafc",
    },
  },
  logo: {
    width: 80,
    height: 32,
    objectFit: "contain",
    borderRadius: 4,
    backgroundColor: "#f5f5f5",
    padding: 4,
  },
  actionBtn: {
    minWidth: "auto",
    padding: "4px 8px",
    borderRadius: 6,
    fontWeight: 600,
    fontSize: "0.8rem",
    textTransform: "none",
  },
  paginationBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "12px 16px",
    borderTop: "1px solid #f1f5f9",
    backgroundColor: "#fff",
    borderRadius: "0 0 12px 12px",
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "48px 24px",
    color: "#999",
  },
  loadingBox: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "40px 0",
    justifyContent: "center",
  },
  cardsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
    gap: 24,
    marginTop: 24,
  },
  integrationCard: {
    borderRadius: 16,
    border: "1px solid #e5e7eb",
    backgroundColor: "#fff",
    transition: "all 0.3s ease",
    position: "relative",
    overflow: "visible",
    "&:hover": {
      boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
      transform: "translateY(-4px)",
    },
  },
  cardHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    marginBottom: 16,
  },
  iconWrapper: {
    width: 64,
    height: 64,
    borderRadius: 16,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 12,
  },
  cardTitle: {
    fontSize: "1.125rem",
    fontWeight: 700,
    color: "#1f2937",
    marginBottom: 8,
  },
  cardDescription: {
    fontSize: "0.875rem",
    color: "#6b7280",
    marginBottom: 16,
    lineHeight: 1.5,
  },
  featureList: {
    listStyle: "none",
    padding: 0,
    margin: "16px 0",
    "& li": {
      display: "flex",
      alignItems: "center",
      gap: 8,
      marginBottom: 8,
      fontSize: "0.875rem",
      color: "#4b5563",
    },
  },
  statusBadge: {
    position: "absolute",
    top: 16,
    right: 16,
    borderRadius: 20,
    padding: "4px 12px",
    fontSize: "0.75rem",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  activeBadge: {
    backgroundColor: "#d1fae5",
    color: "#065f46",
  },
  inactiveBadge: {
    backgroundColor: "#fee2e2",
    color: "#991b1b",
  },
  configButton: {
    borderRadius: 8,
    textTransform: "none",
    fontWeight: 600,
    padding: "8px 16px",
    width: "100%",
  },
  advancedButton: {
    borderRadius: 8,
    textTransform: "none",
    fontWeight: 600,
    padding: "8px 16px",
    width: "100%",
    marginTop: 8,
  },
}));

const QueueIntegration = () => {
  const classes = useStyles();
  const theme = useTheme();
  const sidebarColor = theme.palette.primary.main || "#3b82f6";

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [defaultIntegrationType, setDefaultIntegrationType] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [googleModalOpen, setGoogleModalOpen] = useState(false);
  const [flowBuilderModalOpen, setFlowBuilderModalOpen] = useState(false);
  const [flowBuilderIntegrationId, setFlowBuilderIntegrationId] = useState(null);
  const [fbIntegrationModalOpen, setFbIntegrationModalOpen] = useState(false);
  const [fbIntegrationId, setFbIntegrationId] = useState(null);
  const [webhookModalOpen, setWebhookModalOpen] = useState(false);
  const [braipIntegracao, setBraipIntegracao] = useState(null);
  const [paytIntegracao, setPaytIntegracao] = useState(null);
  const [conectorAberto, setConectorAberto] = useState(false);
  const [webhookIntegrationId, setWebhookIntegrationId] = useState(null);
  const [asaasModalOpen, setAsaasModalOpen] = useState(false);
  const [mercadoPagoModalOpen, setMercadoPagoModalOpen] = useState(false);
  const [sgpModalOpen, setSgpModalOpen] = useState(false);
  const [sgpConfig, setSgpConfig] = useState(null);
  const [sysClinicaModalOpen, setSysClinicaModalOpen] = useState(false);
  const [sysClinicaConfig, setSysClinicaConfig] = useState(null);
  const [sysAcademiaModalOpen, setSysAcademiaModalOpen] = useState(false);
  const [sysAcademiaConfig, setSysAcademiaConfig] = useState(null);
  const [hostiSitesModalOpen, setHostiSitesModalOpen] = useState(false);
  const [hostiSitesConfig, setHostiSitesConfig] = useState(null);
  const [infinityPayModalOpen, setInfinityPayModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [googleIntegration, setGoogleIntegration] = useState(null);
  const [loadingGoogleIntegration, setLoadingGoogleIntegration] = useState(false);
  const [sheetsStatus, setSheetsStatus] = useState({ connected: false, email: null, sheets: [] });
  const [sheetsUrlInput, setSheetsUrlInput] = useState("");
  const [sheetsAddingUrl, setSheetsAddingUrl] = useState(false);
  const [sheetsExpanded, setSheetsExpanded] = useState(false);
  const [paymentSettings, setPaymentSettings] = useState([]);
  const [loadingPaymentSettings, setLoadingPaymentSettings] = useState(false);
  const [queueIntegration, dispatch] = useReducer(reducer, []);
  const { user, socket } = useContext(AuthContext);
  const [tablePage, setTablePage] = useState(0);
  const rowsPerPage = 10;

  const { getPlanCompany } = usePlans();
  const companyId = user.companyId;
  const [intVisibility, setIntVisibility] = useState(null);
  const DEFAULT_HIDDEN_INT = ["sgp", "sysclinica", "sysacademia", "hostisites"];
  const showInt = (key) => {
    if (user?.super) return true;
    if (intVisibility && Object.prototype.hasOwnProperty.call(intVisibility, key)) return intVisibility[key] !== false;
    return !DEFAULT_HIDDEN_INT.includes(key);
  };
  useEffect(() => {
    api.get("/public-settings/integrationsVisibility").then(({ data }) => {
      let raw = data;
      if (raw && typeof raw === "object" && "value" in raw) raw = raw.value;
      try { setIntVisibility(raw ? JSON.parse(raw) : {}); } catch (e) { setIntVisibility({}); }
    }).catch(() => setIntVisibility({}));
  }, []);
  const history = useHistory();

  useEffect(() => {
    async function fetchData() {
      const planConfigs = await getPlanCompany(undefined, companyId);
      if (!planConfigs.plan.useIntegrations) {
        toast.error("Esta empresa não possui permissão para acessar essa página! Estamos lhe redirecionando.");
        setTimeout(() => {
          history.push(`/`)
        }, 1000);
      }
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    const fetchGoogleIntegration = async () => {
      try {
        setLoadingGoogleIntegration(true);
        const { data } = await api.get("/google-calendar/integrations");
        setGoogleIntegration(data);
      } catch (err) {
        toastError(err);
      } finally {
        setLoadingGoogleIntegration(false);
      }
    };

    const fetchPaymentSettings = async () => {
      try {
        setLoadingPaymentSettings(true);
        const { data } = await api.get("/payment-settings");
        setPaymentSettings(data);
      } catch (err) {
        toastError(err);
      } finally {
        setLoadingPaymentSettings(false);
      }
    };

    const fetchSgpConfig = async () => {
      try {
        const { data } = await api.get("/sgp-integration");
        setSgpConfig(data || null);
      } catch { }
    };

    const fetchSysClinicaConfig = async () => {
      try {
        const { data } = await api.get("/sys-clinica-integration");
        setSysClinicaConfig(data || null);
      } catch { }
    };

    const fetchSysAcademiaConfig = async () => {
      try {
        const { data } = await api.get("/sys-academia-integration");
        setSysAcademiaConfig(data || null);
      } catch { }
    };

    const fetchHostiSitesConfig = async () => {
      try {
        const { data } = await api.get("/hosti-sites-integration");
        setHostiSitesConfig(data || null);
      } catch { }
    };

    fetchGoogleIntegration();
    fetchPaymentSettings();
    fetchSgpConfig();
    fetchSysClinicaConfig();
    fetchSysAcademiaConfig();
    fetchHostiSitesConfig();

    api.get("/google-sheets/status").then(({ data }) => setSheetsStatus(data)).catch(() => {});
  }, []);

  const handleReloadPaymentSettings = () => {
    const fetchPaymentSettings = async () => {
      try {
        setLoadingPaymentSettings(true);
        const { data } = await api.get("/payment-settings");
        setPaymentSettings(data);
      } catch (err) {
        toastError(err);
      } finally {
        setLoadingPaymentSettings(false);
      }
    };
    fetchPaymentSettings();
  };

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchIntegrations = async () => {
        try {
          const { data } = await api.get("/queueIntegration/", {
            params: { searchParam, pageNumber },
          });
          dispatch({ type: "LOAD_INTEGRATIONS", payload: data.queueIntegrations });
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          toastError(err);
        }
      };
      fetchIntegrations();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber]);

  useEffect(() => {
    const onQueueEvent = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_INTEGRATIONS", payload: data.queueIntegration });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_INTEGRATION", payload: +data.integrationId });
      }
    };

    socket.on(`company-${companyId}-queueIntegration`, onQueueEvent);
    return () => {
      socket.off(`company-${companyId}-queueIntegration`, onQueueEvent);
    };
  }, [socket, companyId]);

  const handleOpenUserModal = () => {
    setSelectedIntegration(null);
    setUserModalOpen(true);
  };

  const handleOpenGoogleModal = () => {
    setGoogleModalOpen(true);
  };

  const handleCloseIntegrationModal = () => {
    setSelectedIntegration(null);
    setDefaultIntegrationType(null);
    setUserModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditIntegration = (queueIntegration) => {
    setSelectedIntegration(queueIntegration);
    setUserModalOpen(true);
  };

  const handleDisconnectGoogle = async () => {
    try {
      await api.delete("/google-calendar/integration");
      toast.success("Google Calendar desconectado com sucesso.");
      setGoogleIntegration([]);
    } catch (err) {
      toastError(err);
    }
  };

  const handleSheetsConnect = async () => {
    try {
      const { data } = await api.get("/google-sheets/auth-url");
      window.open(data.url, "_blank", "width=600,height=700");
      // Polling para detectar quando o usuário autorizou
      const poll = setInterval(async () => {
        try {
          const res = await api.get("/google-sheets/status");
          if (res.data.connected) {
            setSheetsStatus(res.data);
            clearInterval(poll);
            toast.success("Google Sheets conectado com sucesso!");
          }
        } catch { clearInterval(poll); }
      }, 3000);
      setTimeout(() => clearInterval(poll), 120000);
    } catch (err) {
      toastError(err);
    }
  };

  const handleSheetsDisconnect = async () => {
    try {
      await api.delete("/google-sheets/disconnect");
      setSheetsStatus({ connected: false, email: null, sheets: [] });
      toast.success("Google Sheets desconectado.");
    } catch (err) {
      toastError(err);
    }
  };

  const handleSheetsAddSheet = async () => {
    if (!sheetsUrlInput.trim()) return;
    setSheetsAddingUrl(true);
    try {
      const { data } = await api.post("/google-sheets/sheets", { spreadsheetUrl: sheetsUrlInput });
      setSheetsStatus(prev => ({ ...prev, sheets: [...prev.sheets, data] }));
      setSheetsUrlInput("");
      toast.success("Planilha adicionada!");
    } catch (err) {
      toastError(err);
    } finally {
      setSheetsAddingUrl(false);
    }
  };

  const handleSheetsRemoveSheet = async (sheetId) => {
    try {
      await api.delete(`/google-sheets/sheets/${sheetId}`);
      setSheetsStatus(prev => ({ ...prev, sheets: prev.sheets.filter(s => s.id !== sheetId) }));
      toast.success("Planilha removida.");
    } catch (err) {
      toastError(err);
    }
  };

  const handleDeleteIntegration = async (integrationId) => {
    try {
      await api.delete(`/queueIntegration/${integrationId}`);
      toast.success(i18n.t("queueIntegration.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingUser(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  // Recuperacao de venda pela Braip. Usa tabela e webhook proprios, por isso
  // nao entra na lista de queueIntegration como as demais.
  const buscarBraip = useCallback(async () => {
    try {
      const { data } = await api.get("/gateway-integrations");
      const achada = (data?.integracoes || []).find(i => i.provider === "braip");
      setBraipIntegracao(achada || null);
    } catch (e) {
      /* a tela nao pode quebrar por causa deste bloco */
    }
  }, []);

  useEffect(() => { buscarBraip(); }, [buscarBraip]);

  const ativarBraip = async () => {
    try {
      await api.post("/gateway-integrations", { provider: "braip" });
      await buscarBraip();
      toast.success("Braip ativada. Copie a URL e cadastre no painel da Braip.");
    } catch (err) {
      toastError(err);
    }
  };

  const copiarUrlBraip = () => {
    if (!braipIntegracao) return;
    const url = `${process.env.REACT_APP_BACKEND_URL || ""}/gateway/braip/${braipIntegracao.token}`;
    navigator.clipboard.writeText(url);
    toast.success("URL de webhook copiada.");
  };

  // Recuperacao de venda pela Payt. Mesmo caminho da Braip: tabela e webhook
  // proprios, so muda o tradutor que le o aviso no backend.
  const buscarPayt = useCallback(async () => {
    try {
      const { data } = await api.get("/gateway-integrations");
      const achada = (data?.integracoes || []).find(i => i.provider === "payt");
      setPaytIntegracao(achada || null);
    } catch (e) {
      /* a tela nao pode quebrar por causa deste bloco */
    }
  }, []);

  useEffect(() => { buscarPayt(); }, [buscarPayt]);

  const ativarPayt = async () => {
    try {
      await api.post("/gateway-integrations", { provider: "payt" });
      await buscarPayt();
      toast.success("Payt ativada. Copie a URL e cadastre como postback no painel da Payt.");
    } catch (err) {
      toastError(err);
    }
  };

  const copiarUrlPayt = () => {
    if (!paytIntegracao) return;
    const url = `${process.env.REACT_APP_BACKEND_URL || ""}/gateway/payt/${paytIntegracao.token}`;
    navigator.clipboard.writeText(url);
    toast.success("URL de webhook copiada.");
  };

  // Desativar nao apaga nada: a URL continua a mesma e volta a valer ao reativar.
  const desativarGateway = async (integracao, recarregar, nome) => {
    if (!integracao) return;
    const ok = window.confirm(
      `Desativar a ${nome}? Os avisos que chegarem deixam de disparar fluxo. Se reativar, a URL continua a mesma.`
    );
    if (!ok) return;
    try {
      await api.delete(`/gateway-integrations/${integracao.id}`);
      await recarregar();
      toast.success(`${nome} desativada.`);
    } catch (err) {
      toastError(err);
    }
  };

  const getIntegrationLogo = (type) => {
    if (type === "dialogflow") return dialogflow;
    if (type === "n8n") return "https://n8n.io/brandguidelines/logo-dark.svg";
    if (type === "braip") return braipLogo;
    if (type === "payt") return paytLogo;
    if (type === "webhook") return webhooks;
    if (type === "typebot") return "https://raw.githubusercontent.com/baptisteArno/typebot.io/main/apps/builder/public/images/logo.png";
    if (type === "flowbuilder") return flowbuilder;
    if (type === "hostinotas") return "https://hostinotas.com.br/assets/img/logo.png";
    if (type === "asaas") return `${process.env.REACT_APP_BACKEND_URL}/public/asaas-logo.svg`;
    if (type === "mercadopago") return "https://http2.mlstatic.com/frontend-assets/dx-template-lib/assets/logo-mercadopago.svg";
    if (type === "infinitypay") return "https://cdn.prod.website-files.com/65c1399ac999a342139b5069/65c1399ac999a342139b5434_logo_brlc_preto.svg";
    if (type === "googlecalendar") return "https://www.gstatic.com/images/branding/product/2x/calendar_2020q4_48dp.png";
    if (type === "googlesheets") return "https://www.gstatic.com/images/branding/product/2x/sheets_2020q4_48dp.png";
    return webhooks;
  };

  const getIntegrationInfo = (type) => {
    const integrations = {
      dialogflow: {
        color: "#FF9800",
        bgColor: "#FFF3E0",
        title: "Dialogflow",
        description: "Integre com o Dialogflow do Google para criar conversas inteligentes com IA",
        features: ["IA conversacional", "Processamento de linguagem natural", "Múltiplos idiomas"],
      },
      n8n: {
        color: "#EA4B71",
        bgColor: "#FCE4EC",
        title: "n8n",
        description: "Automatize fluxos de trabalho conectando diferentes aplicações e serviços",
        features: ["Automação de workflows", "Integração com 200+ apps", "Webhooks personalizados"],
      },
      braip: {
        color: "#6D36FB",
        bgColor: "#F3EEFF",
        title: "Braip",
        description: "Recupere vendas perdidas com fluxos automáticos a partir dos eventos da Braip",
        features: [
          "Carrinho abandonado",
          "Pix e boleto não pagos",
          "Compra aprovada e recusada",
          "Código de rastreio"
        ],
      },
      payt: {
        color: "#E67A20",
        bgColor: "#FDF0E4",
        title: "Payt",
        description: "Recupere vendas perdidas com fluxos automáticos a partir dos eventos da Payt",
        features: [
          "Carrinho abandonado",
          "Pix e boleto não pagos",
          "Compra aprovada e recusada",
          "Código de rastreio"
        ],
      },
      webhook: {
        color: "#00BCD4",
        bgColor: "#E0F7FA",
        title: "Webhook",
        description: "Conecte sistemas externos via HTTP para enviar e receber dados em tempo real",
        features: ["Requisições HTTP", "Dados em tempo real", "Flexibilidade total"],
      },
      typebot: {
        color: "#7C3AED",
        bgColor: "#EDE9FE",
        title: "Typebot",
        description: "Crie chatbots conversacionais visuais sem código com interface drag-and-drop",
        features: ["Editor visual", "Sem código", "Fluxos personalizados"],
      },
      flowbuilder: {
        color: "#10B981",
        bgColor: "#D1FAE5",
        title: "Flow Builder",
        description: "Construtor de fluxos interno para criar automações de atendimento personalizadas",
        features: ["Editor de fluxos", "Condições lógicas", "Variáveis dinâmicas"],
      },
      hostinotas: {
        color: "#2563EB",
        bgColor: "#DBEAFE",
        title: "Hosti Notas",
        description: "Sistema de emissão fiscal integrado ao seu CRM",
        features: ["Emissão de NF-e", "Emissão de NFS-e", "Integração automática"],
      },
      asaas: {
        color: "#FF6B35",
        bgColor: "#FFF4ED",
        title: "Asaas",
        description: "Gateway de pagamento para boletos, pix e cartões",
        features: ["Boletos bancários", "Pagamento via PIX", "Cartão de crédito", "Carnê digital"],
      },
      mercadopago: {
        color: "#00AEEF",
        bgColor: "#E6F7FF",
        title: "Mercado Pago",
        description: "Plataforma de pagamentos da Mercado Livre",
        features: ["Pagamentos online", "PIX", "Cartões", "Link de pagamento"],
      },
      sgp: {
        color: "#0369a1",
        bgColor: "#e0f2fe",
        title: "SGP",
        description: "Sistema de Gestão de Provedor — consulte clientes, faturas e abra chamados via API",
        features: ["Consulta por CPF/CNPJ", "2ª via boleto/PIX", "Abertura de chamados", "Desbloqueio de acesso"],
      },
      sysclinica: {
        color: "#065f46",
        bgColor: "#d1fae5",
        title: "Sys Clinica",
        description: "Sistema de gestão de clínica — integre agendamentos, pacientes e atendimentos via API",
        features: ["Gestão de pacientes", "Agendamentos", "Prontuários", "Atendimento integrado"],
      },
      sysacademia: {
        color: "#1565c0",
        bgColor: "#e3f2fd",
        title: "Sys Academia",
        description: "Sistema de gestão de academia — integre alunos, mensalidades e aulas via API",
        features: ["Gestão de alunos", "Controle de mensalidades", "Agendamento de aulas", "Atendimento integrado"],
      },
      hostisites: {
        color: "#92400e",
        bgColor: "#fef3c7",
        title: "Hosti Sites",
        description: "Integre o Hosti Sites ao Floow Chat para gerenciar e automatizar ações nos seus sites",
        features: ["Gestão de sites", "Automação de ações", "Notificações integradas", "API conectada"],
      },
      infinitypay: {
        color: "#6d28d9",
        bgColor: "#ede9fe",
        title: "InfinityPay",
        description: "Gateway de pagamento — gere links de checkout com cartão e PIX automaticamente",
        features: ["Link de pagamento", "Cartão de crédito", "PIX", "Webhook automático"],
      },
      openai: {
        color: "#10a37f",
        bgColor: "#e6f7f3",
        title: "OpenAI",
        description: "Integre modelos GPT-4 para IA conversacional, geração de texto e automação inteligente",
        features: ["GPT-4 e GPT-3.5", "Geração de texto", "Análise de conteúdo"],
      },
      gemini: {
        color: "#4285f4",
        bgColor: "#e8f0fe",
        title: "Google Gemini",
        description: "Use os modelos de IA generativa do Google para potencializar seu atendimento",
        features: ["Gemini Pro e Ultra", "Multimodal (texto e imagem)", "Respostas inteligentes"],
      },
      grok: {
        color: "#1d9bf0",
        bgColor: "#e8f5fd",
        title: "Grok (xAI)",
        description: "IA da xAI com acesso a informações em tempo real e respostas diretas",
        features: ["Respostas em tempo real", "Análise de dados", "Integração com X"],
      },
      openrouter: {
        color: "#6d28d9",
        bgColor: "#ede9fe",
        title: "OpenRouter",
        description: "Acesse 300+ modelos de IA (GPT, Claude, Gemini, LLaMA) com uma única chave API",
        features: ["300+ modelos disponíveis", "GPT, Claude, Gemini, LLaMA", "Uma única chave para tudo"],
      },
    };
    return integrations[type] || integrations.webhook;
  };

  if (user.profile === "user") {
    return <ForbiddenPage />;
  }

  const paginatedItems = queueIntegration
    .filter(integration => integration.type !== "flowbuilder" && integration.type !== "webhook")
    .slice(
      tablePage * rowsPerPage,
      tablePage * rowsPerPage + rowsPerPage
    );
  const totalPages = Math.ceil(queueIntegration.length / rowsPerPage);

  return (
    <Box className={classes.root} onScroll={handleScroll} style={{ "--sidebar-color": sidebarColor }}>
      <ConfirmationModal
        title={
          deletingUser &&
          `${i18n.t("queueIntegration.confirmationModal.deleteTitle")} ${deletingUser.name}?`
        }
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteIntegration(deletingUser.id)}
      >
        {i18n.t("queueIntegration.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <IntegrationModal
        open={userModalOpen}
        onClose={handleCloseIntegrationModal}
        aria-labelledby="form-dialog-title"
        integrationId={selectedIntegration && selectedIntegration.id}
        defaultType={defaultIntegrationType}
      />
      <GoogleCalendarIntegrationModal
        open={googleModalOpen}
        onClose={() => setGoogleModalOpen(false)}
      />
      <FlowBuilderModal
        open={flowBuilderModalOpen}
        integrationId={flowBuilderIntegrationId}
        onClose={() => {
          setFlowBuilderModalOpen(false);
          setFlowBuilderIntegrationId(null);
          setSearchParam("");
          setPageNumber(1);
        }}
        onSave={() => {
          setFlowBuilderModalOpen(false);
          setFlowBuilderIntegrationId(null);
          setSearchParam("");
          setPageNumber(1);
        }}
      />
      <WebhookModal
        open={webhookModalOpen}
        integrationId={webhookIntegrationId}
        onClose={() => {
          setWebhookModalOpen(false);
          setWebhookIntegrationId(null);
          setSearchParam("");
          setPageNumber(1);
        }}
        onSave={() => {
          setWebhookModalOpen(false);
          setWebhookIntegrationId(null);
          setSearchParam("");
          setPageNumber(1);
        }}
      />
      <AsaasModal
        open={asaasModalOpen}
        onClose={() => setAsaasModalOpen(false)}
        onSave={() => {
          setAsaasModalOpen(false);
          handleReloadPaymentSettings();
          setSearchParam("");
          setPageNumber(1);
        }}
      />
      <FlowBuilderIntegrationModal
        open={fbIntegrationModalOpen}
        integrationId={fbIntegrationId}
        onClose={() => { setFbIntegrationModalOpen(false); setFbIntegrationId(null); }}
        onSave={() => { setFbIntegrationModalOpen(false); setFbIntegrationId(null); }}
      />

      <MercadoPagoModal
        open={mercadoPagoModalOpen}
        onClose={() => setMercadoPagoModalOpen(false)}
        onSave={() => {
          setMercadoPagoModalOpen(false);
          handleReloadPaymentSettings();
          setSearchParam("");
          setPageNumber(1);
        }}
      />

      <SgpModal
        open={sgpModalOpen}
        onClose={() => setSgpModalOpen(false)}
        onSave={() => {
          setSgpModalOpen(false);
          api.get("/sgp-integration").then(({ data }) => setSgpConfig(data || null)).catch(() => {});
        }}
      />

      <SysClinicaModal
        open={sysClinicaModalOpen}
        onClose={() => setSysClinicaModalOpen(false)}
        onSave={() => {
          setSysClinicaModalOpen(false);
          api.get("/sys-clinica-integration").then(({ data }) => setSysClinicaConfig(data || null)).catch(() => {});
        }}
      />

      <SysAcademiaModal
        open={sysAcademiaModalOpen}
        onClose={() => setSysAcademiaModalOpen(false)}
        onSave={() => {
          setSysAcademiaModalOpen(false);
          api.get("/sys-academia-integration").then(({ data }) => setSysAcademiaConfig(data || null)).catch(() => {});
        }}
      />

      <HostiSitesModal
        open={hostiSitesModalOpen}
        onClose={() => setHostiSitesModalOpen(false)}
        onSave={() => {
          setHostiSitesModalOpen(false);
          api.get("/hosti-sites-integration").then(({ data }) => setHostiSitesConfig(data || null)).catch(() => {});
        }}
      />

      <InfinityPayModal
        open={infinityPayModalOpen}
        onClose={() => setInfinityPayModalOpen(false)}
        onSave={() => {
          setInfinityPayModalOpen(false);
          api.get("/payment-settings").then(({ data }) => setPaymentSettings(data || [])).catch(() => {});
        }}
      />

      {/* Header */}
      <Box className={classes.header}>
        <Box className={classes.headerLeft}>
          <Box>
            <Typography className={classes.headerTitle}>
              {i18n.t("queueIntegration.title")}
            </Typography>
            <Typography className={classes.headerSubtitle}>
              {queueIntegration.length} {queueIntegration.length === 1 ? "integração conectada" : "integrações conectadas"}
            </Typography>
          </Box>
        </Box>
        <Box className={classes.headerRight}>
          <TextField
            size="small"
            variant="outlined"
            placeholder="Pesquisar..."
            value={searchParam}
            onChange={handleSearch}
            className={classes.searchField}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: "#999" }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Box>

      {/* Content */}
      <Box className={classes.content}>
        {loading && queueIntegration.length === 0 ? (
          <Box className={classes.loadingBox}>
            <CircularProgress size={32} />
            <Typography variant="body2">Carregando integrações...</Typography>
          </Box>
        ) : (
          <>
            <Box className={classes.cardsGrid}>
              {/* Card Hosti Notas - Visível apenas para companyId 1 */}
              {companyId === 1 && (() => {
                const info = getIntegrationInfo("hostinotas");
                const hasHostiNotas = queueIntegration.some(i => i.type === "hostinotas");
                return (
                  <Card className={classes.integrationCard}>
                    <CardContent style={{ padding: 24, position: "relative" }}>
                      {/* Badge de Status */}
                      <Box className={`${classes.statusBadge} ${hasHostiNotas ? classes.activeBadge : classes.inactiveBadge}`}>
                        {hasHostiNotas ? "Ativo" : "Inativo"}
                      </Box>

                      {/* Ícone */}
                      <Box
                        className={classes.iconWrapper}
                        style={{ backgroundColor: info.bgColor }}
                      >
                        <Box
                          style={{
                            width: 48,
                            height: 48,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 28,
                            fontWeight: 700,
                            color: info.color,
                          }}
                        >
                          📄
                        </Box>
                      </Box>

                      {/* Título */}
                      <Typography className={classes.cardTitle}>
                        {info.title}
                      </Typography>

                      {/* Descrição */}
                      <Typography className={classes.cardDescription}>
                        {info.description}
                      </Typography>

                      {/* Features */}
                      <ul className={classes.featureList}>
                        {info.features.map((feature, idx) => (
                          <li key={idx}>
                            <CheckCircleIcon style={{ fontSize: 16, color: info.color }} />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>

                    <CardActions style={{ padding: "0 24px 24px", display: "flex", flexDirection: "column", gap: 8 }}>
                      {/* Botão Configurar */}
                      <Button
                        variant="contained"
                        className={classes.configButton}
                        color="primary"
                        startIcon={<SettingsIcon />}
                        onClick={() => window.open("https://hostinotas.com.br", "_blank")}
                      >
                        {hasHostiNotas ? "Configurar" : "Ativar Integração"}
                      </Button>


                      {hasHostiNotas && (
                        <>
                          {/* Botão Configurações Avançadas */}
                          <Button
                            variant="outlined"
                            className={classes.advancedButton}
                            color="primary"
                            startIcon={<MoreVertIcon />}
                            onClick={() => window.open("https://hostinotas.com.br", "_blank")}
                          >
                            Configurações Avançadas
                          </Button>
                        </>
                      )}
                    </CardActions>
                  </Card>
                );
              })()}

              {/* Card Braip - recuperação de venda */}
              {(() => {
                const info = getIntegrationInfo("braip");
                const ativa = !!braipIntegracao?.ativo;
                return (
                  <Card className={classes.integrationCard}>
                    <CardContent style={{ padding: 24, position: "relative" }}>
                      <Box className={`${classes.statusBadge} ${ativa ? classes.activeBadge : classes.inactiveBadge}`}>
                        {ativa ? "Ativo" : "Inativo"}
                      </Box>

                      <Box className={classes.iconWrapper} style={{ backgroundColor: info.bgColor }}>
                        <img
                          src={getIntegrationLogo("braip")}
                          alt="braip"
                          style={{ width: 48, height: 48, objectFit: "contain" }}
                        />
                      </Box>

                      <Typography className={classes.cardTitle}>{info.title}</Typography>
                      <Typography className={classes.cardDescription}>{info.description}</Typography>

                      <ul className={classes.featureList}>
                        {info.features.map((feature, idx) => (
                          <li key={idx}>
                            <CheckCircleIcon style={{ fontSize: 16, color: info.color }} />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>

                    <CardActions style={{ padding: "0 24px 24px", display: "flex", flexDirection: "column", gap: 8 }}>
                      {!ativa ? (
                        <Button
                          variant="contained"
                          className={classes.configButton}
                          color="primary"
                          startIcon={<SettingsIcon />}
                          onClick={ativarBraip}
                        >
                          Ativar Integração
                        </Button>
                      ) : (
                        <Button
                          variant="contained"
                          className={classes.configButton}
                          color="primary"
                          startIcon={<FileCopyIcon />}
                          onClick={copiarUrlBraip}
                        >
                          Copiar URL de webhook
                        </Button>
                      )}
                      {ativa && (
                        <Button
                          size="small"
                          style={{ color: "#64748b" }}
                          onClick={() => desativarGateway(braipIntegracao, buscarBraip, "Braip")}
                        >
                          Desativar
                        </Button>
                      )}
                    </CardActions>
                  </Card>
                );
              })()}

              {/* Card Payt - recuperação de venda */}
              {(() => {
                const info = getIntegrationInfo("payt");
                const ativa = !!paytIntegracao?.ativo;
                return (
                  <Card className={classes.integrationCard}>
                    <CardContent style={{ padding: 24, position: "relative" }}>
                      <Box className={`${classes.statusBadge} ${ativa ? classes.activeBadge : classes.inactiveBadge}`}>
                        {ativa ? "Ativo" : "Inativo"}
                      </Box>

                      <Box className={classes.iconWrapper} style={{ backgroundColor: info.bgColor }}>
                        <img
                          src={getIntegrationLogo("payt")}
                          alt="payt"
                          style={{ width: 48, height: 48, objectFit: "contain" }}
                        />
                      </Box>

                      <Typography className={classes.cardTitle}>{info.title}</Typography>
                      <Typography className={classes.cardDescription}>{info.description}</Typography>

                      <ul className={classes.featureList}>
                        {info.features.map((feature, idx) => (
                          <li key={idx}>
                            <CheckCircleIcon style={{ fontSize: 16, color: info.color }} />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>

                    <CardActions style={{ padding: "0 24px 24px", display: "flex", flexDirection: "column", gap: 8 }}>
                      {!ativa ? (
                        <Button
                          variant="contained"
                          className={classes.configButton}
                          color="primary"
                          startIcon={<SettingsIcon />}
                          onClick={ativarPayt}
                        >
                          Ativar Integração
                        </Button>
                      ) : (
                        <>
                          <Button
                            variant="contained"
                            className={classes.configButton}
                            color="primary"
                            startIcon={<FileCopyIcon />}
                            onClick={copiarUrlPayt}
                          >
                            Copiar URL de webhook
                          </Button>
                          <Button
                            size="small"
                            style={{ color: "#64748b" }}
                            onClick={() => desativarGateway(paytIntegracao, buscarPayt, "Payt")}
                          >
                            Desativar
                          </Button>
                        </>
                      )}
                    </CardActions>
                  </Card>
                );
              })()}


              {/* Card Assistentes de IA (MCP) — o mesmo conector serve para Claude, Codex e outros */}
              <Card className={classes.integrationCard}>
                <CardContent style={{ padding: 24, position: "relative" }}>
                  <Box className={`${classes.statusBadge} ${classes.activeBadge}`}>Disponível</Box>
                  <Box className={classes.iconWrapper} style={{ width: "auto", gap: 8 }}>
                    <img src={logoCodex} alt="ChatGPT e Codex" title="ChatGPT e Codex" style={{ width: 48, height: 48, borderRadius: 12, objectFit: "cover" }} />
                    <img src={logoClaude} alt="Claude" title="Claude" style={{ width: 48, height: 48, borderRadius: 12, objectFit: "cover" }} />
                  </Box>
                  <Typography className={classes.cardTitle}>Assistentes de IA (MCP)</Typography>
                  <Typography className={classes.cardDescription}>
                    Conecte o Claude, o Codex ou outro assistente de IA à sua conta e monte tudo conversando
                  </Typography>
                  <ul className={classes.featureList}>
                    {[
                      "Cria e ajusta fluxos",
                      "Agentes de IA e base de conhecimento",
                      "Relatórios de atendimento e recuperação",
                      "Nada vai para os clientes sem você ligar"
                    ].map((feature, idx) => (
                      <li key={idx}>
                        <CheckCircleIcon style={{ fontSize: 16, color: "#2563EB" }} />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
                <CardActions style={{ padding: "0 24px 24px", display: "flex", flexDirection: "column", gap: 8 }}>
                  <Button
                    variant="contained"
                    className={classes.configButton}
                    color="primary"
                    startIcon={<SettingsIcon />}
                    onClick={() => setConectorAberto(true)}
                  >
                    Como conectar
                  </Button>
                </CardActions>
              </Card>
              <ConectorAssistentes open={conectorAberto} onClose={() => setConectorAberto(false)} />

              {/* Card FlowBuilder - Sempre visível */}
              {(() => {
                const info = getIntegrationInfo("flowbuilder");
                if (!showInt("flowbuilder")) return null;
                const hasFlowBuilder = queueIntegration.some(i => i.type === "flowbuilder");
                return (
                  <Card className={classes.integrationCard}>
                    <CardContent style={{ padding: 24, position: "relative" }}>
                      {/* Badge de Status */}
                      <Box className={`${classes.statusBadge} ${hasFlowBuilder ? classes.activeBadge : classes.inactiveBadge}`}>
                        {hasFlowBuilder ? "Ativo" : "Inativo"}
                      </Box>

                      {/* Ícone */}
                      <Box
                        className={classes.iconWrapper}
                        style={{ backgroundColor: info.bgColor }}
                      >
                        <img
                          src={getIntegrationLogo("flowbuilder")}
                          alt="flowbuilder"
                          style={{ width: 48, height: 48, objectFit: "contain" }}
                        />
                      </Box>

                      {/* Título */}
                      <Typography className={classes.cardTitle}>
                        {info.title}
                      </Typography>

                      {/* Descrição */}
                      <Typography className={classes.cardDescription}>
                        {info.description}
                      </Typography>

                      {/* Features */}
                      <ul className={classes.featureList}>
                        {info.features.map((feature, idx) => (
                          <li key={idx}>
                            <CheckCircleIcon style={{ fontSize: 16, color: info.color }} />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>

                    <CardActions style={{ padding: "0 24px 24px", display: "flex", flexDirection: "column", gap: 8 }}>
                      {!hasFlowBuilder ? (
                        <Button
                          variant="contained"
                          className={classes.configButton}
                          color="primary"
                          startIcon={<SettingsIcon />}
                          onClick={() => { setFbIntegrationId(null); setFbIntegrationModalOpen(true); }}
                        >
                          Ativar Integração
                        </Button>
                      ) : (
                        <>
                          <Button
                            variant="contained"
                            className={classes.configButton}
                            color="primary"
                            startIcon={<SettingsIcon />}
                            onClick={() => {
                              const fb = queueIntegration.find(i => i.type === "flowbuilder");
                              setFbIntegrationId(fb?.id || null);
                              setFbIntegrationModalOpen(true);
                            }}
                          >
                            Configurar
                          </Button>
                          <Button
                            size="small"
                            style={{ color: "#ef4444", marginTop: 4 }}
                            startIcon={<DeleteOutlineIcon />}
                            onClick={async () => {
                              const allFb = queueIntegration.filter(i => i.type === "flowbuilder");
                              if (!allFb.length) return;
                              try {
                                await Promise.all(allFb.map(fb => api.delete(`/queueIntegration/${fb.id}`)));
                                toast.success("Integração Flow Builder desativada!");
                              } catch (err) {
                                toastError(err);
                              }
                            }}
                          >
                            Desativar
                          </Button>
                        </>
                      )}
                    </CardActions>
                  </Card>
                );
              })()}

              {/* Card Webhook - Sempre visível */}
              {(() => {
                const info = getIntegrationInfo("webhook");
                if (!showInt("webhook")) return null;
                const hasWebhook = queueIntegration.some(i => i.type === "webhook");
                return (
                  <Card className={classes.integrationCard}>
                    <CardContent style={{ padding: 24, position: "relative" }}>
                      {/* Badge de Status */}
                      <Box className={`${classes.statusBadge} ${hasWebhook ? classes.activeBadge : classes.inactiveBadge}`}>
                        {hasWebhook ? "Ativo" : "Inativo"}
                      </Box>

                      {/* Ícone */}
                      <Box
                        className={classes.iconWrapper}
                        style={{ backgroundColor: info.bgColor }}
                      >
                        <img
                          src={getIntegrationLogo("webhook")}
                          alt="webhook"
                          style={{ width: 48, height: 48, objectFit: "contain" }}
                        />
                      </Box>

                      {/* Título */}
                      <Typography className={classes.cardTitle}>
                        {info.title}
                      </Typography>

                      {/* Descrição */}
                      <Typography className={classes.cardDescription}>
                        {info.description}
                      </Typography>

                      {/* Features */}
                      <ul className={classes.featureList}>
                        {info.features.map((feature, idx) => (
                          <li key={idx}>
                            <CheckCircleIcon style={{ fontSize: 16, color: info.color }} />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>

                    <CardActions style={{ padding: "0 24px 24px", display: "flex", flexDirection: "column", gap: 8 }}>
                      {/* Botão Configurar */}
                      <Button
                        variant="contained"
                        className={classes.configButton}
                        color="primary"
                        startIcon={<SettingsIcon />}
                        onClick={() => {
                          const webhookIntegration = queueIntegration.find(i => i.type === "webhook");
                          if (webhookIntegration) {
                            setWebhookIntegrationId(webhookIntegration.id);
                          } else {
                            setWebhookIntegrationId(null);
                          }
                          setWebhookModalOpen(true);
                        }}
                      >
                        {hasWebhook ? "Configurar" : "Ativar Integração"}
                      </Button>

                      {hasWebhook && (
                        <>
                          {/* Botão Desativar (só aparece quando ativo) */}
                          <Button
                            size="small"
                            style={{ color: "#ef4444", marginTop: 8 }}
                            startIcon={<DeleteOutlineIcon />}
                            onClick={(e) => {
                              e.stopPropagation();
                              const webhookIntegration = queueIntegration.find(i => i.type === "webhook");
                              if (webhookIntegration) {
                                setConfirmModalOpen(true);
                                setDeletingUser(webhookIntegration);
                              }
                            }}
                          >
                            Desativar
                          </Button>
                        </>
                      )}
                    </CardActions>
                  </Card>
                );
              })()}

              {/* Card Google Calendar - Sempre visível */}
              {(() => {
                const hasGoogleCalendar = googleIntegration && googleIntegration.length > 0;
                if (!showInt("googlecalendar")) return null;
                return (
                  <Card className={classes.integrationCard}>
                    <CardContent style={{ padding: 24, position: "relative" }}>
                      {/* Badge de Status */}
                      <Box className={`${classes.statusBadge} ${hasGoogleCalendar ? classes.activeBadge : classes.inactiveBadge}`}>
                        {hasGoogleCalendar ? "Ativo" : "Inativo"}
                      </Box>

                      {/* Ícone */}
                      <Box
                        className={classes.iconWrapper}
                        style={{ backgroundColor: "#E3F2FD" }}
                      >
                        <img
                          src={getIntegrationLogo("googlecalendar")}
                          alt="Google Calendar"
                          style={{ width: 48, height: 48, objectFit: "contain" }}
                        />
                      </Box>

                      {/* Título */}
                      <Typography className={classes.cardTitle}>
                        Google Calendar
                      </Typography>

                      {/* Descrição */}
                      <Typography className={classes.cardDescription}>
                        Sincronize seus agendamentos e eventos com o Google Calendar
                      </Typography>

                      {/* Features */}
                      <ul className={classes.featureList}>
                        <li>
                          <CheckCircleIcon style={{ fontSize: 16, color: "#1976D2" }} />
                          <span>Sincronização automática</span>
                        </li>
                        <li>
                          <CheckCircleIcon style={{ fontSize: 16, color: "#1976D2" }} />
                          <span>Agendamentos integrados</span>
                        </li>
                        <li>
                          <CheckCircleIcon style={{ fontSize: 16, color: "#1976D2" }} />
                          <span>Notificações em tempo real</span>
                        </li>
                      </ul>
                    </CardContent>

                    <CardActions style={{ padding: "0 24px 24px", display: "flex", flexDirection: "column", gap: 8 }}>
                      {/* Botão Configurar */}
                      <Button
                        variant="contained"
                        className={classes.configButton}
                        color="primary"
                        startIcon={<SettingsIcon />}
                        onClick={() => setGoogleModalOpen(true)}
                      >
                        {hasGoogleCalendar ? "Configurar" : "Ativar Integração"}
                      </Button>

                      {hasGoogleCalendar && (
                        <>
                          {/* Botão Desativar (só aparece quando ativo) */}
                          <Button
                            size="small"
                            style={{ color: "#ef4444", marginTop: 8 }}
                            startIcon={<DeleteOutlineIcon />}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDisconnectGoogle();
                            }}
                          >
                            Desativar
                          </Button>
                        </>
                      )}
                    </CardActions>
                  </Card>
                );
              })()}

              {/* Card Google Sheets */}
              {(() => {
                const hasSheets = sheetsStatus.connected;
                if (!showInt("sheets")) return null;
                return (
                  <Card className={classes.integrationCard}>
                    <CardContent style={{ padding: 24, position: "relative" }}>
                      <Box className={`${classes.statusBadge} ${hasSheets ? classes.activeBadge : classes.inactiveBadge}`}>
                        {hasSheets ? "Ativo" : "Inativo"}
                      </Box>
                      <Box className={classes.iconWrapper} style={{ backgroundColor: "#E8F5E9" }}>
                        <img
                          src={getIntegrationLogo("googlesheets")}
                          alt="Google Sheets"
                          style={{ width: 48, height: 48, objectFit: "contain" }}
                        />
                      </Box>
                      <Typography className={classes.cardTitle}>Google Sheets</Typography>
                      <Typography className={classes.cardDescription}>
                        Conecte várias planilhas do Google à sua empresa e utilize nos fluxos de atendimento
                      </Typography>
                      {sheetsStatus.accounts?.length > 0 && (
                        <Box style={{ marginTop: 8 }}>
                          {sheetsStatus.accounts.map(acc => (
                            <Typography key={acc.id} style={{ fontSize: 12, color: "#4caf50" }}>
                              ✓ {acc.email}
                            </Typography>
                          ))}
                        </Box>
                      )}
                      <ul className={classes.featureList}>
                        <li><CheckCircleIcon style={{ fontSize: 16, color: "#388e3c" }} /><span>Múltiplas contas Google</span></li>
                        <li><CheckCircleIcon style={{ fontSize: 16, color: "#388e3c" }} /><span>Importar arquivo CSV/XLSX</span></li>
                        <li><CheckCircleIcon style={{ fontSize: 16, color: "#388e3c" }} /><span>Integração com fluxos</span></li>
                      </ul>
                      {sheetsStatus.sheets?.length > 0 && (
                        <Typography style={{ fontSize: 12, color: "#666", marginTop: 4 }}>
                          {sheetsStatus.sheets.length} planilha(s) conectada(s)
                        </Typography>
                      )}
                    </CardContent>
                    <CardActions style={{ padding: "0 24px 24px", display: "flex", flexDirection: "column", gap: 8 }}>
                      <Button variant="contained" color="primary" className={classes.configButton} startIcon={<SettingsIcon />} onClick={handleSheetsConnect}>
                        Gerenciar Planilhas
                      </Button>
                      {hasSheets && (
                        <Button
                          size="small"
                          style={{ color: "#ef4444", marginTop: 4 }}
                          startIcon={<DeleteOutlineIcon />}
                          onClick={handleSheetsDisconnect}
                        >
                          Desativar
                        </Button>
                      )}
                    </CardActions>
                  </Card>
                );
              })()}

              {/* Card Asaas - Sempre visível */}
              {(() => {
                const info = getIntegrationInfo("asaas");
                if (!showInt("asaas")) return null;
                const asaasConfig = paymentSettings.find(item => item.provider === "asaas");
                const hasAsaas = asaasConfig && asaasConfig.active;
                return (
                  <Card className={classes.integrationCard}>
                    <CardContent style={{ padding: 24, position: "relative" }}>
                      {/* Badge de Status */}
                      <Box className={`${classes.statusBadge} ${hasAsaas ? classes.activeBadge : classes.inactiveBadge}`}>
                        {hasAsaas ? "Ativo" : "Inativo"}
                      </Box>

                      {/* Ícone */}
                      <Box style={{ backgroundColor: "#ffffff", border: "1px solid #f0e8e0", borderRadius: 12, padding: "10px 16px", marginBottom: 12, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
                        <img
                          src={getIntegrationLogo("asaas")}
                          alt="Asaas"
                          style={{ height: 36, width: "auto", maxWidth: 140, objectFit: "contain" }}
                        />
                      </Box>

                      {/* Título */}
                      <Typography className={classes.cardTitle}>
                        {info.title}
                      </Typography>

                      {/* Descrição */}
                      <Typography className={classes.cardDescription}>
                        {info.description}
                      </Typography>

                      {/* Features */}
                      <ul className={classes.featureList}>
                        {info.features.map((feature, idx) => (
                          <li key={idx}>
                            <CheckCircleIcon style={{ fontSize: 16, color: info.color }} />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>

                    <CardActions style={{ padding: "0 24px 24px", display: "flex", flexDirection: "column", gap: 8 }}>
                      {/* Botão Configurar */}
                      <Button
                        variant="contained"
                        className={classes.configButton}
                        color="primary"
                        startIcon={<SettingsIcon />}
                        onClick={() => setAsaasModalOpen(true)}
                      >
                        {asaasConfig ? "Configurar" : "Ativar Integração"}
                      </Button>
                      {hasAsaas && (
                        <Button
                          size="small"
                          style={{ color: "#ef4444", marginTop: 4 }}
                          startIcon={<DeleteOutlineIcon />}
                          onClick={async () => {
                            try {
                              await api.delete(`/payment-settings/${asaasConfig.id}`);
                              setPaymentSettings(prev => prev.filter(s => s.id !== asaasConfig.id));
                              toast.success("Asaas desativado.");
                            } catch (err) {
                              toastError(err);
                            }
                          }}
                        >
                          Desativar
                        </Button>
                      )}
                    </CardActions>
                  </Card>
                );
              })()}

              {/* Card Mercado Pago - Sempre visível */}
              {(() => {
                const info = getIntegrationInfo("mercadopago");
                if (!showInt("mercadopago")) return null;
                const mercadoPagoConfig = paymentSettings.find(item => item.provider === "mercadopago");
                const hasMercadoPago = mercadoPagoConfig && mercadoPagoConfig.active;
                return (
                  <Card className={classes.integrationCard}>
                    <CardContent style={{ padding: 24, position: "relative" }}>
                      {/* Badge de Status */}
                      <Box className={`${classes.statusBadge} ${hasMercadoPago ? classes.activeBadge : classes.inactiveBadge}`}>
                        {hasMercadoPago ? "Ativo" : "Inativo"}
                      </Box>

                      {/* Ícone */}
                      <Box style={{ backgroundColor: "#ffffff", border: "1px solid #dbeafe", borderRadius: 12, padding: "10px 16px", marginBottom: 12, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
                        <img
                          src={getIntegrationLogo("mercadopago")}
                          alt="Mercado Pago"
                          style={{ height: 36, width: "auto", maxWidth: 140, objectFit: "contain" }}
                        />
                      </Box>

                      {/* Título */}
                      <Typography className={classes.cardTitle}>
                        {info.title}
                      </Typography>

                      {/* Descrição */}
                      <Typography className={classes.cardDescription}>
                        {info.description}
                      </Typography>

                      {/* Features */}
                      <ul className={classes.featureList}>
                        {info.features.map((feature, idx) => (
                          <li key={idx}>
                            <CheckCircleIcon style={{ fontSize: 16, color: info.color }} />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>

                    <CardActions style={{ padding: "0 24px 24px", display: "flex", flexDirection: "column", gap: 8 }}>
                      {/* Botão Configurar */}
                      <Button
                        variant="contained"
                        className={classes.configButton}
                        color="primary"
                        startIcon={<SettingsIcon />}
                        onClick={() => setMercadoPagoModalOpen(true)}
                      >
                        {mercadoPagoConfig ? "Configurar" : "Ativar Integração"}
                      </Button>
                    </CardActions>
                  </Card>
                );
              })()}

              {/* Card InfinityPay - Sempre visível */}
              {(() => {
                const info = getIntegrationInfo("infinitypay");
                if (!showInt("infinitypay")) return null;
                const ipConfig = paymentSettings.find(item => item.provider === "infinitypay");
                const hasIp = ipConfig && ipConfig.active;
                return (
                  <Card className={classes.integrationCard}>
                    <CardContent style={{ padding: 24, position: "relative" }}>
                      <Box className={`${classes.statusBadge} ${hasIp ? classes.activeBadge : classes.inactiveBadge}`}>
                        {hasIp ? "Ativo" : "Inativo"}
                      </Box>
                      <Box style={{ backgroundColor: info.bgColor, borderRadius: 12, padding: "10px 16px", marginBottom: 12, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
                        <img
                          src={getIntegrationLogo("infinitypay")}
                          alt="InfinityPay"
                          style={{ height: 36, width: "auto", maxWidth: 140, objectFit: "contain" }}
                        />
                      </Box>
                      <Typography className={classes.cardTitle}>{info.title}</Typography>
                      <Typography className={classes.cardDescription}>{info.description}</Typography>
                      <ul className={classes.featureList}>
                        {info.features.map((feature, idx) => (
                          <li key={idx}>
                            <CheckCircleIcon style={{ fontSize: 16, color: info.color }} />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                    <CardActions style={{ padding: "0 24px 24px", display: "flex", flexDirection: "column", gap: 8 }}>
                      <Button
                        variant="contained"
                        className={classes.configButton}
                        color="primary"
                        startIcon={<SettingsIcon />}
                        onClick={() => setInfinityPayModalOpen(true)}
                      >
                        {ipConfig ? "Configurar" : "Ativar Integração"}
                      </Button>
                      {hasIp && (
                        <Button
                          size="small"
                          style={{ color: "#ef4444", marginTop: 4 }}
                          startIcon={<DeleteOutlineIcon />}
                          onClick={async () => {
                            try {
                              await api.delete(`/payment-settings/${ipConfig.id}`);
                              setPaymentSettings(prev => prev.filter(s => s.id !== ipConfig.id));
                              toast.success("InfinityPay desativado.");
                            } catch (err) {
                              toastError(err);
                            }
                          }}
                        >
                          Desativar
                        </Button>
                      )}
                    </CardActions>
                  </Card>
                );
              })()}

              {/* Card SGP - Sempre visível */}
              {(() => {
                const info = getIntegrationInfo("sgp");
                if (!showInt("sgp")) return null;
                const hasSgp = sgpConfig && sgpConfig.ativo;
                return (
                  <Card className={classes.integrationCard}>
                    <CardContent style={{ padding: 24, position: "relative" }}>
                      <Box className={`${classes.statusBadge} ${hasSgp ? classes.activeBadge : classes.inactiveBadge}`}>
                        {hasSgp ? "Ativo" : "Inativo"}
                      </Box>

                      <Box
                        className={classes.iconWrapper}
                        style={{ backgroundColor: info.bgColor }}
                      >
                        <Box
                          style={{
                            width: 48,
                            height: 48,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 20,
                            fontWeight: 700,
                            color: info.color,
                            letterSpacing: -1,
                          }}
                        >
                          SGP
                        </Box>
                      </Box>

                      <Typography className={classes.cardTitle}>
                        {info.title}
                      </Typography>

                      <Typography className={classes.cardDescription}>
                        {info.description}
                      </Typography>

                      <ul className={classes.featureList}>
                        {info.features.map((feature, idx) => (
                          <li key={idx}>
                            <CheckCircleIcon style={{ fontSize: 16, color: info.color }} />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>

                    <CardActions style={{ padding: "0 24px 24px", display: "flex", flexDirection: "column", gap: 8 }}>
                      <Button
                        variant="contained"
                        className={classes.configButton}
                        color="primary"
                        startIcon={<SettingsIcon />}
                        onClick={() => setSgpModalOpen(true)}
                      >
                        {sgpConfig ? "Configurar" : "Ativar Integração"}
                      </Button>

                      {hasSgp && (
                        <Button
                          size="small"
                          style={{ color: "#ef4444", marginTop: 4 }}
                          startIcon={<DeleteOutlineIcon />}
                          onClick={async () => {
                            try {
                              await api.delete("/sgp-integration");
                              setSgpConfig(null);
                              toast.success("Integração SGP removida.");
                            } catch (err) {
                              toastError(err);
                            }
                          }}
                        >
                          Remover
                        </Button>
                      )}
                    </CardActions>
                  </Card>
                );
              })()}

              {/* Card Sys Clinica - Sempre visível */}
              {(() => {
                const info = getIntegrationInfo("sysclinica");
                if (!showInt("sysclinica")) return null;
                const hasSysClinica = sysClinicaConfig && sysClinicaConfig.ativo;
                return (
                  <Card className={classes.integrationCard}>
                    <CardContent style={{ padding: 24, position: "relative" }}>
                      <Box className={`${classes.statusBadge} ${hasSysClinica ? classes.activeBadge : classes.inactiveBadge}`}>
                        {hasSysClinica ? "Ativo" : "Inativo"}
                      </Box>

                      <Box
                        className={classes.iconWrapper}
                        style={{ backgroundColor: info.bgColor }}
                      >
                        <Box
                          style={{
                            width: 48,
                            height: 48,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 13,
                            fontWeight: 700,
                            color: info.color,
                            letterSpacing: -0.5,
                          }}
                        >
                          SYS
                        </Box>
                      </Box>

                      <Typography className={classes.cardTitle}>
                        {info.title}
                      </Typography>

                      <Typography className={classes.cardDescription}>
                        {info.description}
                      </Typography>

                      <ul className={classes.featureList}>
                        {info.features.map((feature, idx) => (
                          <li key={idx}>
                            <CheckCircleIcon style={{ fontSize: 16, color: info.color }} />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>

                    <CardActions style={{ padding: "0 24px 24px", display: "flex", flexDirection: "column", gap: 8 }}>
                      <Button
                        variant="contained"
                        className={classes.configButton}
                        color="primary"
                        startIcon={<SettingsIcon />}
                        onClick={() => setSysClinicaModalOpen(true)}
                      >
                        {sysClinicaConfig ? "Configurar" : "Ativar Integração"}
                      </Button>

                      {hasSysClinica && (
                        <Button
                          size="small"
                          style={{ color: "#ef4444", marginTop: 4 }}
                          startIcon={<DeleteOutlineIcon />}
                          onClick={async () => {
                            try {
                              await api.delete("/sys-clinica-integration");
                              setSysClinicaConfig(null);
                              toast.success("Integração Sys Clinica removida.");
                            } catch (err) {
                              toastError(err);
                            }
                          }}
                        >
                          Remover
                        </Button>
                      )}
                    </CardActions>
                  </Card>
                );
              })()}


              {/* Card Sys Academia - Sempre visível */}
              {(() => {
                const info = getIntegrationInfo("sysacademia");
                if (!showInt("sysacademia")) return null;
                const hasSysAcademia = sysAcademiaConfig && sysAcademiaConfig.ativo;
                return (
                  <Card className={classes.integrationCard}>
                    <CardContent style={{ padding: 24, position: "relative" }}>
                      <Box className={`${classes.statusBadge} ${hasSysAcademia ? classes.activeBadge : classes.inactiveBadge}`}>
                        {hasSysAcademia ? "Ativo" : "Inativo"}
                      </Box>

                      <Box
                        className={classes.iconWrapper}
                        style={{ backgroundColor: info.bgColor }}
                      >
                        <Box
                          style={{
                            width: 48,
                            height: 48,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 11,
                            fontWeight: 700,
                            color: info.color,
                            letterSpacing: -0.5,
                            textAlign: "center",
                            lineHeight: 1.2,
                          }}
                        >
                          SYS<br/>ACK
                        </Box>
                      </Box>

                      <Typography className={classes.cardTitle}>
                        {info.title}
                      </Typography>

                      <Typography className={classes.cardDescription}>
                        {info.description}
                      </Typography>

                      <ul className={classes.featureList}>
                        {info.features.map((feature, idx) => (
                          <li key={idx}>
                            <CheckCircleIcon style={{ fontSize: 16, color: info.color }} />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>

                    <CardActions style={{ padding: "0 24px 24px", display: "flex", flexDirection: "column", gap: 8 }}>
                      <Button
                        variant="contained"
                        className={classes.configButton}
                        color="primary"
                        startIcon={<SettingsIcon />}
                        onClick={() => setSysAcademiaModalOpen(true)}
                      >
                        {sysAcademiaConfig ? "Configurar" : "Ativar Integração"}
                      </Button>

                      {hasSysAcademia && (
                        <Button
                          size="small"
                          style={{ color: "#ef4444", marginTop: 4 }}
                          startIcon={<DeleteOutlineIcon />}
                          onClick={async () => {
                            try {
                              await api.delete("/sys-academia-integration");
                              setSysAcademiaConfig(null);
                              toast.success("Integração Sys Academia removida.");
                            } catch (err) {
                              toastError(err);
                            }
                          }}
                        >
                          Remover
                        </Button>
                      )}
                    </CardActions>
                  </Card>
                );
              })()}

              {/* Card Hosti Sites - Sempre visível */}
              {(() => {
                const info = getIntegrationInfo("hostisites");
                if (!showInt("hostisites")) return null;
                const hasHostiSites = hostiSitesConfig && hostiSitesConfig.ativo;
                return (
                  <Card className={classes.integrationCard}>
                    <CardContent style={{ padding: 24, position: "relative" }}>
                      <Box className={`${classes.statusBadge} ${hasHostiSites ? classes.activeBadge : classes.inactiveBadge}`}>
                        {hasHostiSites ? "Ativo" : "Inativo"}
                      </Box>

                      <Box
                        className={classes.iconWrapper}
                        style={{ backgroundColor: info.bgColor }}
                      >
                        <Box
                          style={{
                            width: 48,
                            height: 48,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 11,
                            fontWeight: 700,
                            color: info.color,
                            letterSpacing: -0.5,
                            textAlign: "center",
                            lineHeight: 1.2,
                          }}
                        >
                          HST<br/>SIT
                        </Box>
                      </Box>

                      <Typography className={classes.cardTitle}>
                        {info.title}
                      </Typography>

                      <Typography className={classes.cardDescription}>
                        {info.description}
                      </Typography>

                      <ul className={classes.featureList}>
                        {info.features.map((feature, idx) => (
                          <li key={idx}>
                            <CheckCircleIcon style={{ fontSize: 16, color: info.color }} />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>

                    <CardActions style={{ padding: "0 24px 24px", display: "flex", flexDirection: "column", gap: 8 }}>
                      <Button
                        variant="contained"
                        className={classes.configButton}
                        color="primary"
                        startIcon={<SettingsIcon />}
                        onClick={() => setHostiSitesModalOpen(true)}
                      >
                        {hostiSitesConfig ? "Configurar" : "Ativar Integração"}
                      </Button>

                      {hasHostiSites && (
                        <Button
                          size="small"
                          style={{ color: "#ef4444", marginTop: 4 }}
                          startIcon={<DeleteOutlineIcon />}
                          onClick={async () => {
                            try {
                              await api.delete("/hosti-sites-integration");
                              setHostiSitesConfig(null);
                              toast.success("Integração Hosti Sites removida.");
                            } catch (err) {
                              toastError(err);
                            }
                          }}
                        >
                          Remover
                        </Button>
                      )}
                    </CardActions>
                  </Card>
                );
              })()}

              {/* Cards de integrações existentes */}
              {paginatedItems.map((integration) => {
                const info = getIntegrationInfo(integration.type);
                return (
                  <Card key={integration.id} className={classes.integrationCard}>
                    <CardContent style={{ padding: 24, position: "relative" }}>
                      {/* Badge de Status */}
                      <Box className={`${classes.statusBadge} ${classes.activeBadge}`}>
                        Ativo
                      </Box>

                      {/* Ícone */}
                      <Box
                        className={classes.iconWrapper}
                        style={{ backgroundColor: info.bgColor }}
                      >
                        <img
                          src={getIntegrationLogo(integration.type)}
                          alt={integration.type}
                          style={{ width: 48, height: 48, objectFit: "contain" }}
                        />
                      </Box>

                      {/* Título */}
                      <Typography className={classes.cardTitle}>
                        {integration.name}
                      </Typography>

                      {/* Descrição */}
                      <Typography className={classes.cardDescription}>
                        {info.description}
                      </Typography>

                      {/* Features */}
                      <ul className={classes.featureList}>
                        {info.features.map((feature, idx) => (
                          <li key={idx}>
                            <CheckCircleIcon style={{ fontSize: 16, color: info.color }} />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </CardContent>

                    <CardActions style={{ padding: "0 24px 24px", display: "flex", flexDirection: "column", gap: 8 }}>
                      {/* Botão Configurar */}
                      <Button
                        variant="contained"
                        className={classes.configButton}
                        color="primary"
                        startIcon={<SettingsIcon />}
                        onClick={() => handleEditIntegration(integration)}
                      >
                        Configurar
                      </Button>

                      {/* Botão Configurações Avançadas ou Templates (FlowBuilder) */}
                      <Button
                        variant="outlined"
                        className={classes.advancedButton}
                        color="primary"
                        startIcon={<MoreVertIcon />}
                        onClick={() => {
                          if (integration.type === "flowbuilder") {
                            window.open("https://hostibr.com.br/templates", "_blank");
                          } else {
                            handleEditIntegration(integration);
                          }
                        }}
                      >
                        {integration.type === "flowbuilder" ? "Veja templates avançados" : "Configurações Avançadas"}
                      </Button>

                      {/* Botão Excluir */}
                      <Button
                        size="small"
                        style={{ color: "#ef4444", marginTop: 8 }}
                        startIcon={<DeleteOutlineIcon />}
                        onClick={(e) => {
                          e.stopPropagation();
                          setConfirmModalOpen(true);
                          setDeletingUser(integration);
                        }}
                      >
                        Excluir Integração
                      </Button>
                    </CardActions>
                  </Card>
                );
              })}
            </Box>

            {/* Paginação */}
            {queueIntegration.length > rowsPerPage && (
              <Box style={{ display: "flex", justifyContent: "center", marginTop: 32, gap: 8 }}>
                <Button
                  size="small"
                  disabled={tablePage === 0}
                  onClick={() => setTablePage(tablePage - 1)}
                  className={classes.actionBtn}
                >
                  Anterior
                </Button>
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  let pageIdx = i;
                  if (totalPages > 5) {
                    const start = Math.max(0, Math.min(tablePage - 2, totalPages - 5));
                    pageIdx = start + i;
                  }
                  return (
                    <Button
                      key={pageIdx}
                      size="small"
                      variant={pageIdx === tablePage ? "contained" : "text"}
                      color={pageIdx === tablePage ? "primary" : "default"}
                      onClick={() => setTablePage(pageIdx)}
                      style={{
                        minWidth: 32,
                        borderRadius: 6,
                        fontWeight: pageIdx === tablePage ? 700 : 400,
                      }}
                    >
                      {pageIdx + 1}
                    </Button>
                  );
                })}
                <Button
                  size="small"
                  disabled={tablePage >= totalPages - 1}
                  onClick={() => setTablePage(tablePage + 1)}
                  className={classes.actionBtn}
                >
                  Próximo
                </Button>
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default QueueIntegration;